import Vue from 'vue'
import { debounce } from 'util/ts-utils'
import { callTurnToConfigFn } from 'util/turnto-utils'
import domUtils from 'util/dom-utils'
import globalConfig from 'init/global.config'
import globalStore, { Store } from 'init/global-store'
import logger from 'util/logger'
import paginateMixin from 'mixins/paginate-mixin'
import reviewsStore from 'store/reviews-store'
import gridLayoutDataMixin from 'layouts/grid/grid-layout-data-mixin'
import { RestError } from 'services/api-service'
import reviewsListConfig, { RelatedReviewsDisplayTypes }
  from 'widgets/reviews-list/reviews-list-widget.config'
import reviewsListService from 'services/api/reviews-list'
import Review from 'components/review/review'
import ReviewsListUtilities from 'components/reviews-list/reviews-list-utilities'

import withRender from './reviews-list.html'
import './reviews-list.scss'

const MIN_REVIEWS_FOR_TOOLBAR = 3

export default withRender(Vue.extend({
  data() {
    return {
      filterTotal: 0,
      previousSku: '',
      reviews: [],
      reviewsStore,
      showMoreDisabled: false,
      store: globalStore,
      total: 0
    }
  },
  components: {
    'tt-review': Review,
    'tt-reviews-list-utilities': ReviewsListUtilities
  },
  mixins: [gridLayoutDataMixin, paginateMixin],
  computed: {
    currentSku(): string {
      return (this.store as Store).sku
    },
    filtersQueryString() {
      return this.reviewsStore.filterStore.encodedFiltersUrl
    },
    hasMoreRslts() {
      // the "hasMore" in paginate-mixin does not take applied filters into account
      if (this.isFiltered) {
        return this.reviews.length < this.filterTotal
      }
      return this.hasMore
    },
    hasSearchTerm() {
      return this.reviewsStore.trimmedSearchTerm.length > 0
    },
    heading() {
      const totalItems = this.totalReviews
      const numPages = this.numberOfPages
      const level = reviewsListConfig.headingLevel
      if (numPages <= 1) {
        const total = this.isFiltered ? this.filterTotal : this.total
        const text = this.$msg('reviewsList.numberOfReviews', [total], total)
        return { ariaText: text, text, level }
      }
      const startItem = reviewsListConfig.useShowMore ? 1 : this.offset + 1
      let endItem = this.reviews.length // when using "show more"
      if (!this.reviewsListConfig.useShowMore) {
        const itemsPerPage = this.offset === 0 ? this.initialPageSize : this.pageSize
        endItem = this.currentPage < numPages ? ((startItem - 1) + itemsPerPage) : totalItems
      }
      return {
        ariaText: this.$msg('reviewsList.numberOfReviews.paged.aria',
          [startItem, endItem, totalItems], totalItems),
        text: this.$msg('reviewsList.numberOfReviews.paged',
          [startItem, endItem, totalItems], totalItems),
        level
      }
    },
    isRelatedIncluded() {
      return reviewsListConfig.relatedReviewsDisplay === RelatedReviewsDisplayTypes.INCLUDED
    },
    mediaSize() {
      return this.reviewsListConfig.breakpoints[this.currentLayoutIndex || 0].mediaSize
    },
    reviewsListConfig() {
      return reviewsListConfig
    },
    showUtilities() {
      // not to be shown if less than the min number of reviews or filters have been applied
      return this.total >= MIN_REVIEWS_FOR_TOOLBAR || this.isFiltered
    },
    sortBy() {
      return this.reviewsStore.sort
    },
    truncateLength() {
      return this.reviewsListConfig.breakpoints[this.currentLayoutIndex || 0].truncateLength
    },
    searchTerm() {
      return this.reviewsStore.trimmedSearchTerm
    },
    isFiltered() {
      return this.reviewsStore.filterStore.appliedFilters.length > 0 ||
        !!this.reviewsStore.trimmedSearchTerm
    },
    totalReviews() {
      return this.isFiltered ? this.filterTotal : this.total
    },
    numberOfPages() {
      const totalItems = this.totalReviews
      if (this.pageSize === 0 || totalItems === 0) {
        return 0
      }
      if (totalItems <= this.initialPageSize) {
        return 1
      }
      const totalLessInitialPageSize = totalItems - this.initialPageSize
      return Math.ceil(totalLessInitialPageSize / this.pageSize) + (this.initialPageSize ? 1 : 0)
    },
    noFilterResults() {
      return this.isFiltered && this.filterTotal === 0
    }
  },
  methods: {
    fetchData: debounce(function cb(isPageChange) {
      if (this.currentSku && (this.offset === 0 || this.offset <= this.totalReviews)) {
        const limit =
          this.offset === 0 ? this.initialPageSize : this.pageSize
        if (reviewsListConfig.useShowMore && this.offset !== 0) {
          this.showMoreDisabled = true
        }
        const isFirstCall = this.currentSku !== this.previousSku
        reviewsListService.reviewsList({
          sku: this.currentSku,
          offset: this.offset,
          limit,
          filters: this.filtersQueryString,
          sort: !this.hasSearchTerm ? this.sortBy : 'NONE',
          includeRelated: this.isRelatedIncluded,
          includeFilters: isFirstCall, // only include 1st time
          searchText: this.hasSearchTerm ? encodeURIComponent(this.searchTerm) : ''
        }).then((data) => {
          this.previousSku = this.currentSku
          globalStore.catItemId = data.catalogItemId
          globalStore.parentCategoryIds = data.parentCategoryIds
          globalStore.brand = data.brand
          if (isFirstCall && data.filters) {
            reviewsStore.filterStore.filters = data.filters
          }
          if (data.productReviewsJsonLdEnabled && isFirstCall) {
            this.$emit('reviewsLoaded', data.reviews, data.catalogItemTitle)
          }
          if (this.isFiltered) {
            this.filterTotal = data.total
          } else {
            this.total = data.total
            this.filterTotal = 0
          }
          if (reviewsListConfig.useShowMore && this.offset !== 0) {
            data.reviews.forEach((review) => {
              this.reviews.push(review)
            })
            this.showMoreDisabled = false
          } else {
            this.reviews = data.reviews
          }

          this.$nextTick(() => {
            if (isPageChange) {
              // not first time, so may have paginated.  Will only scroll up, not down
              // TT-11837 - focus header after scroll. safeFocus in case client hides the header
              const focusFn = () => domUtils.safeFocus(() => this.$refs.headingText)
              domUtils
                .scrollUpToRef(this.$refs, 'reviewsList', globalConfig.additionalPagingScroll)
                .then(focusFn)
                .catch(focusFn)
            }
            // IF the client has these function defined under turnToConfig.reviewsList, they will be
            // called
            isFirstCall ? callTurnToConfigFn('reviewsList', 'onFinish') :
              callTurnToConfigFn('reviewsList', 'onUpdate')
          })
        }).catch((e: RestError) => {
          logger.info('Error loading reviews list widget', e)
        })
      }
    }, 200),
    openMediaModal(media, startingIndex = 0) {
      // passing it up one more level
      this.$emit('openMediaModal', media, startingIndex)
    }
  },
  created() {
    // initialPageSize and pageSize are in paginateMixin
    this.initialPageSize = reviewsListConfig.initialPageSize || reviewsListConfig.pageSize
    this.pageSize = reviewsListConfig.pageSize
    // setLayoutConfig method in gridLayoutDataMixin
    this.setLayoutsConfig(reviewsListConfig.breakpoints)
    this.reviewsStore.sort = reviewsListConfig.defaultSortOption
    this.fetchData()
  },
  watch: {
    currentSku() {
      this.reviewsStore.sort = reviewsListConfig.defaultSortOption
      this.reviewsStore.searchTerm = ''
      this.reviewsStore.filterStore.clearFilters()
      // method in paginateMixin
      this.onQueryChange()
    },
    searchTerm() {
      this.onQueryChange()
    }
  }
}))
